import { type HTMLAttributes, type Ref } from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../../lib/utils";
import { EmptyStateMedia } from "./EmptyStateMedia";
import { EmptyStateTitle } from "./EmptyStateTitle";
import { EmptyStateSubtitle } from "./EmptyStateSubtitle";
import { EmptyStateActions } from "./EmptyStateActions";
import { EmptyStateBackdrop, emptyStateFadeClassName } from "./EmptyStateBackdrop";

// Empty-state placeholder -- icon badge, title, subtitle, and an action row (1+ Buttons).
// Modeled on shadcn's stock Empty pattern but with this design system's own naming
// (EmptyState.Subtitle / EmptyState.Actions, matching Item's Title/Description/Actions
// convention). No default title/subtitle text -- always consumer-provided, since "empty" copy
// is inherently context-specific.
//
// Namespaced compound (EmptyState.Backdrop / .Media / .Title / .Subtitle / .Actions), matching the
// same "system of parts the consumer arranges/omits" shape as Item and Button.Group.
export const emptyStateVariants = cva(
  // A Backdrop child is absolutely positioned behind the content, so the root only becomes its
  // containing block / stacking context when one is present -- other empty states are untouched.
  "flex min-w-0 flex-col items-center justify-center text-center has-[[data-slot=empty-state-backdrop]]:relative has-[[data-slot=empty-state-backdrop]]:isolate has-[[data-slot=empty-state-backdrop]]:overflow-hidden",
  {
    variants: {
      variant: {
        // Plain placeholder -- dashed box, e.g. inside an empty table body or list.
        default: "rounded-[calc(var(--radius)-2px)] border border-dashed border-border",
        // No border -- a radial gradient (background token, feathering to transparent) sits behind
        // the content so it stays legible layered over busy content (e.g. a chart). The consumer is
        // responsible for positioning (e.g. `absolute inset-0` over whatever it's covering) -- this
        // variant only controls the backdrop treatment, not layout/placement.
        overlay: emptyStateFadeClassName,
      },
      // Internal spacing only. Width and any height cap stay with the consumer, the same way
      // `overlay` leaves positioning to the consumer.
      size: {
        default: "gap-6 p-6 md:p-12",
        // Single-line placeholder sized for a short, dense slot (e.g. a dashboard block's
        // content area) where the roomier default would dominate the block.
        compact: "gap-3 px-4 py-10",
      },
    },
    defaultVariants: { variant: "default", size: "default" },
  }
);

export interface EmptyStateProps extends HTMLAttributes<HTMLDivElement>, VariantProps<typeof emptyStateVariants> {
  ref?: Ref<HTMLDivElement>;
}

const EmptyStateRoot = ({ className, variant, size, ref, ...props }: EmptyStateProps) => (
  <div
    ref={ref}
    data-slot="empty-state"
    data-variant={variant ?? "default"}
    data-size={size ?? "default"}
    className={cn(emptyStateVariants({ variant, size }), className)}
    {...props}
  />
);

export const EmptyState = Object.assign(EmptyStateRoot, {
  Backdrop: EmptyStateBackdrop,
  Media: EmptyStateMedia,
  Title: EmptyStateTitle,
  Subtitle: EmptyStateSubtitle,
  Actions: EmptyStateActions,
});
